import { FC } from 'react';
import { Alert, Badge, Card, Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import CaresPageHeader from '../shell/cares-page-header';
import { getCaresShellBootstrap } from '../shell/bootstrap';

type HelpTopic = {
  id: string;
  title: string;
  body: string;
};

type RoleGuide = {
  id: string;
  role: string;
  guidance: string;
};

const helpTopics: HelpTopic[] = [
  {
    id: 'coverage',
    title: 'What CARES help covers',
    body: 'This page covers day-to-day product guidance for signed-in CARES users. It does not expose internal project notes, deployment documentation, or repository files.',
  },
  {
    id: 'support-path',
    title: 'How to get help',
    body: 'Start with your company administrator or scheduler when you need help with schedules, permissions, assigned departments, or tenant-specific setup. Contact platform support through your organization when access problems cannot be resolved locally.',
  },
  {
    id: 'access-boundaries',
    title: 'Access and data boundaries',
    body: 'Users can only act within the roles and departments assigned to their account. If you expect to see a schedule, queue, or settings page and it is missing, ask your administrator to confirm your membership and role scope.',
  },
];

const roleGuides: RoleGuide[] = [
  {
    id: 'member',
    role: 'Members',
    guidance: 'Use My Schedule to review assignments, manage requests, and update your own profile settings.',
  },
  {
    id: 'scheduler',
    role: 'Schedulers',
    guidance: 'Use Scheduling for request triage and group-specific review tasks tied to your assigned scheduler scope.',
  },
  {
    id: 'admins',
    role: 'Company and Platform Admins',
    guidance: 'Use Admin, Scheduling, and Admin Settings for company setup, permissions, and operational configuration.',
  },
];

const FaqPage: FC = () => {
  const bootstrap = getCaresShellBootstrap();

  if (!bootstrap.authenticated) {
    return (
      <>
        <CaresPageHeader homepage="FAQ" activepage="FAQ" page="Access Required" />
        <Alert variant="danger" className="custom-card">
          FAQ is available after sign-in.{' '}
          <Link to="/login" className="alert-link">
            Sign in
          </Link>{' '}
          to continue.
        </Alert>
      </>
    );
  }

  return (
    <>
      <CaresPageHeader homepage="Help" activepage="Help" page="CARES Help" />

      <Row className="g-4">
        <Col xl={8}>
          <Card className="custom-card cares-page-hero">
            <Card.Body className="p-4 p-xxl-5">
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                <div>
                  <h2 className="mb-2">CARES Help</h2>
                  <p className="text-muted mb-0">
                    This is the signed-in help surface for CARES users. It provides product guidance without exposing repository documentation or internal implementation notes.
                  </p>
                </div>
                <div className="text-xl-end">
                  <Badge bg="info-transparent" className="text-info fs-12 px-3 py-2">
                    Signed-in users
                  </Badge>
                </div>
              </div>

              <Alert variant="light" className="border mb-4">
                If you need tenant-specific help, contact your company administrator first. They can confirm your assigned role, department membership, and local support path.
              </Alert>

              <div className="d-grid gap-3">
                {helpTopics.map((topic) => (
                  <div key={topic.id} className="border rounded-3 p-3 p-md-4">
                    <h4 className="mb-2">{topic.title}</h4>
                    <p className="text-muted mb-0">{topic.body}</p>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={4}>
          <Card className="custom-card h-100">
            <Card.Body className="p-4">
              <h4 className="mb-3">Role guide</h4>
              <div className="d-grid gap-3">
                {roleGuides.map((guide) => (
                  <div key={guide.id} className="border rounded-3 p-3">
                    <div className="fw-semibold mb-1">{guide.role}</div>
                    <p className="text-muted mb-0 fs-14">{guide.guidance}</p>
                  </div>
                ))}
              </div>

              <div className="border rounded-3 p-3 mt-4 bg-light">
                <div className="fw-semibold mb-1">Access issue?</div>
                <p className="text-muted mb-0 fs-14">
                  Missing pages usually mean your role or department scope has not been assigned for the current account. Ask your administrator or scheduler to review your access.
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default FaqPage;
